import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';

import { BodyLarge400, BodyMedium400, TitleSmall500 } from '@nebula/global-styles';
import { useMediaQuery } from '@nebula/hooks';

import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { publishEditBillDateClick } from '../../../helpers/changeBillingDate/analytics';
import { CurrentAndChosenDateCard, CardSection, DateContainer, EditLink } from './changedDates.styles';
import { PAYMENT_STAGES, PaymentContext } from '../../../components/PaymentOrchestrator/PaymentContextProvider';
import { StyledTitleMedium500 } from './index.styles';

const ChangedDates = ({ disableEditLink = false }) => {
  const { goToPaymentStage } = useContext(PaymentContext);
  const theme = useContext(ThemeContext);
  const isTabletLandscape = useMediaQuery(theme.media.tabletLandscapeMin);
  const BodyText = useMemo(() => (isTabletLandscape ? BodyLarge400 : BodyMedium400), [isTabletLandscape]);
  const TitleText = useMemo(() => (isTabletLandscape ? StyledTitleMedium500 : TitleSmall500), [isTabletLandscape]);

  const { nextBillingDate, selectedDate } = useSelector(state => ({
    nextBillingDate: state.billing?.nextBillingDate,
    selectedDate: state.billingDateSelection?.selectedBillDate,
  }));

  const billingDay = useMemo(() => dayjs(nextBillingDate).format('Do'), [nextBillingDate]);
  const selectedDay = useMemo(() => dayjs(selectedDate).format('Do'), [selectedDate]);

  const handleEditClick = useCallback(() => {
    publishEditBillDateClick();
    goToPaymentStage(PAYMENT_STAGES.CHOOSE_BILLING_DATE);
  }, [goToPaymentStage]);

  const handleEditKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleEditClick();
      }
    },
    [handleEditClick]
  );

  return (
    <CurrentAndChosenDateCard cardType="elevated">
      <CardSection as="section" aria-label={`Current billing date is ${billingDay} of every month`}>
        <DateContainer aria-hidden="true">
          <BodyText>Current date</BodyText>
          <TitleText>{billingDay}</TitleText>
          <BodyText>of every month</BodyText>
        </DateContainer>
      </CardSection>

      <CardSection withBorder as="section" aria-label={`Chosen date is ${selectedDay} of every month`}>
        <DateContainer withMargin aria-hidden="true">
          <BodyText>Chosen date</BodyText>
          <TitleText>{selectedDay}</TitleText>
          <BodyText>of every month</BodyText>
        </DateContainer>

        {!disableEditLink && (
          <EditLink
            tabIndex={0}
            href="#"
            id="change_billing_date_edit"
            onClick={event => {
              event.preventDefault();
              handleEditClick();
            }}
            onKeyDown={handleEditKeyDown}
            aria-label="Edit link to choose a different billing date"
          >
            Edit
          </EditLink>
        )}
      </CardSection>
    </CurrentAndChosenDateCard>
  );
};

ChangedDates.propTypes = {
  disableEditLink: PropTypes.bool,
};

export default ChangedDates;
